/**
 * MatchState - arbitro da partida ONLINE (deathmatch).
 *
 * E o IRMAO do GameState, nao o substituto: o GameState continua sendo o dono da
 * CAMPANHA (ondas PvE) e nao e importado, alterado nem consultado aqui. Os dois
 * seguem o mesmo estilo - maquina de estados explicita, estado publico legivel
 * pela interface, getStatus() sem alocacao, nada lancando dentro do frame.
 *
 * A diferenca de fundo: aqui este modulo NAO simula nada. Quem decide vida,
 * baixa, pontuacao e fim de partida e o servidor; este arquivo guarda o que
 * chegou, mantem o cronometro andando entre dois placares e traduz mensagem de
 * rede em evento do EventBus para a interface desenhar.
 *
 * Estados: 'idle' | 'connecting' | 'lobby' | 'playing' | 'ended'
 *
 * ATENCAO ao integrar (main.js): `isActive()` existe para o modo campanha e o
 * modo online nunca rodarem juntos. Com partida online ativa, o GameState nao
 * pode estar rodando ondas.
 */

import { EVENTS } from '../core/EventBus.js';
import { MATCH, ERR } from '../../shared/netconfig.js';
import { S2C, join as joinMsg, leave as leaveMsg } from '../../shared/protocol.js';

/** Espera maxima pelo socket ficar online, em segundos. */
const CONNECT_TIMEOUT = 12;

/** Espera maxima pela resposta `joined`, em segundos. */
const JOIN_TIMEOUT = 10;

/** Passo da espera pelo socket (fora do laco de frame). */
const POLL_MS = 40;

/** Erros do servidor que derrubam a tentativa de entrar na sala. */
const FATAL_CODES = [ERR.BAD_TOKEN, ERR.NOT_AUTHENTICATED, ERR.ROOM_FULL, ERR.ROOM_NOT_FOUND];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Ordena o placar: pontos, depois baixas, depois menos mortes. */
function byScore(a, b) {
  if (b.score !== a.score) return b.score - a.score;
  if (b.kills !== a.kills) return b.kills - a.kills;
  return a.deaths - b.deaths;
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

export class MatchState {
  constructor(ctx) {
    this.ctx = ctx;

    // A partida online nao congela porque o jogador abriu o menu: o cronometro
    // e o placar continuam vindo do servidor.
    this.updateWhilePaused = true;

    // --- estado publico (a interface le daqui) ---
    /** @type {'idle'|'connecting'|'lobby'|'playing'|'ended'} */
    this.state = 'idle';
    /** @type {{id:string,name:string,mode:string,scoreLimit:number,timeLimit:number}|null} */
    this.room = null;
    /** @type {Map<number, {id:number,nickname:string,kills:number,deaths:number,score:number,ping:number,place:number}>} */
    this.players = new Map();
    /** @type {number|null} id do jogador local nesta partida */
    this.you = null;
    this.mode = MATCH.MODE;
    this.timeLeft = 0;
    this.timeLimit = MATCH.TIME_LIMIT;
    this.scoreLimit = MATCH.SCORE_LIMIT;
    /** @type {Array<object>|null} preenchido no fim da partida */
    this.standings = null;
    this.matchId = null;
    this._joinedOnce = false;
    this.lastReason = '';

    // --- interno ---
    this._offNet = [];
    this._offEvents = [];
    this._joining = false;
    this._bound = false;
    this._rosterDirty = true;
    /** Array reaproveitado do placar ordenado (o HUD le todo frame). */
    this._sorted = [];
    this._warned = new Set();

    // Objeto REAPROVEITADO por getStatus(): o HUD chama isso a cada frame, e um
    // objeto novo por chamada seria lixo garantido 60 vezes por segundo.
    this._status = {
      state: 'idle', mode: MATCH.MODE, active: false, ended: false,
      roomId: null, roomName: '', you: null, nickname: '',
      count: 0, timeLeft: 0, timeLimit: MATCH.TIME_LIMIT, scoreLimit: MATCH.SCORE_LIMIT,
      kills: 0, deaths: 0, score: 0, place: 0, ping: 0,
      leader: null, matchId: null, players: this._sorted
    };
  }

  // =========================================================================
  // ciclo de vida
  // =========================================================================

  async init() {
    const events = this.ctx.events;
    if (events) {
      // Conexao perdida de vez (o NetClient ja gastou todas as tentativas):
      // a partida acabou para este cliente, e a interface precisa saber.
      this._offEvents.push(events.on(EVENTS.NET_STATUS, (p) => this._onNetStatus(p)));
    }
    this._bindNet();
  }

  dispose() {
    for (const off of this._offEvents) { try { off?.(); } catch (err) { this._warnOnce('off', err); } }
    this._offEvents.length = 0;
    for (const off of this._offNet) { try { off?.(); } catch (err) { this._warnOnce('offNet', err); } }
    this._offNet.length = 0;
    this._bound = false;
  }

  // =========================================================================
  // API publica
  // =========================================================================

  /** Ha uma sessao online em andamento (conectando, no lobby, jogando ou no fim). */
  isActive() {
    return this.state !== 'idle';
  }

  /** Vale contar ponto e receber tiro agora. */
  isPlaying() {
    return this.state === 'playing';
  }

  /**
   * Entra numa sala. `roomId` null = o servidor escolhe.
   * Sequencia: conecta com o token da sessao -> espera `joined` -> liga o
   * NetSync -> MATCH_JOINED -> GAME_STATE 'playing'.
   * @returns {Promise<boolean>}
   */
  async joinMatch(roomId = null) {
    if (this._joining) return false;
    if (this.state === 'connecting' || this.state === 'lobby' || this.state === 'playing') return false;

    const net = this.ctx.net;
    if (!net || typeof net.connect !== 'function') {
      this._warnOnce('net', new Error('ctx.net indisponivel'));
      return false;
    }

    const token = this.ctx.session?.token;
    if (!token) {
      this._noticeError(ERR.SERVER_ERROR, null);
    this._emit(EVENTS.NET_ERROR, {
        code: ERR.NOT_AUTHENTICATED,
        message: 'entre na sua conta para jogar online'
      });
      return false;
    }

    // Vindo da tela de fim de partida: limpa o que sobrou antes de entrar de novo.
    if (this.state === 'ended') this._teardown('nova partida');

    this._joining = true;
    try {
      this._setState('connecting');
      this.lastReason = '';
      net.connect(token);

      const online = await this._waitOnline(net, CONNECT_TIMEOUT);
      if (!online) {
        this._giveUp('nao foi possivel conectar ao servidor');
        return false;
      }

      // Ouvinte registrado ANTES do envio: resposta rapida do servidor nao pode
      // chegar antes de existir quem a espere.
      this._bindNet();
      const waitJoined = this._once(S2C.JOINED, JOIN_TIMEOUT);
      net.send(joinMsg(roomId == null ? null : String(roomId), this._mapaLocal()));
      const msg = await waitJoined;
      if (!msg) {
        this._giveUp('o servidor nao respondeu ao pedido de entrada');
        return false;
      }
      // O _onJoined (ouvinte permanente) ja aplicou a mensagem; se por algum
      // motivo ainda estivermos em 'connecting', aplica agora.
      if (this.state === 'connecting') this._applyJoined(msg);
      return this.isActive();
    } catch (err) {
      this._warnOnce('joinMatch', err);
      this._giveUp('falha ao entrar na partida');
      return false;
    } finally {
      this._joining = false;
    }
  }

  /** Sai da partida de proposito: avisa o servidor, limpa a cena e volta ao menu. */
  leaveMatch(reason = 'saiu da partida') {
    const net = this.ctx.net;
    const why = String(reason || 'saiu da partida');

    // O `leave` sai ANTES do disconnect: fechar primeiro deixaria a vaga presa
    // na sala ate o servidor perceber o silencio.
    try { net?.send?.(leaveMsg()); } catch (err) { this._warnOnce('leave', err); }

    this._teardown(why);
    this._setState('idle');
    this.lastReason = why;
    this._emit(EVENTS.MATCH_LEFT, { reason: why });
    this._emit(EVENTS.GAME_STATE, { state: 'menu' });

    try { net?.disconnect?.(why); } catch (err) { this._warnOnce('disconnect', err); }
    return true;
  }

  /** Estado compacto para a interface. SEMPRE o mesmo objeto (sem lixo por frame). */
  getStatus() {
    const s = this._status;
    const me = this.you !== null ? this.players.get(this.you) : null;

    s.state = this.state;
    s.mode = this.mode;
    s.active = this.isActive();
    s.ended = this.state === 'ended';
    s.roomId = this.room?.id ?? null;
    s.roomName = this.room?.name ?? '';
    s.you = this.you;
    s.nickname = me?.nickname || '';
    s.count = this.players.size;
    s.timeLeft = this.timeLeft;
    s.timeLimit = this.timeLimit;
    s.scoreLimit = this.scoreLimit;
    s.kills = me?.kills ?? 0;
    s.deaths = me?.deaths ?? 0;
    s.score = me?.score ?? 0;
    s.place = me?.place ?? 0;
    s.ping = me?.ping ?? num(this.ctx.net?.ping, 0);
    s.matchId = this.matchId;

    const list = this._roster();
    s.players = list;
    s.leader = list.length > 0 ? list[0] : null;
    return s;
  }

  /**
   * Placar ordenado. Array INTERNO reaproveitado (somente leitura) e reconstruido
   * apenas quando algo muda - ordenar por frame seria custo sem motivo.
   */
  _roster() {
    if (!this._rosterDirty) return this._sorted;
    this._rosterDirty = false;
    this._sorted.length = 0;
    for (const p of this.players.values()) this._sorted.push(p);
    this._sorted.sort(byScore);
    for (let i = 0; i < this._sorted.length; i++) this._sorted[i].place = i + 1;
    return this._sorted;
  }

  // =========================================================================
  // laco
  // =========================================================================

  update(dt) {
    try {
      const step = Number.isFinite(dt) && dt > 0 ? dt : 0;

      if (this.state === 'playing') {
        // O servidor manda o tempo restante a cada MATCH.SCORE_BROADCAST; entre
        // duas mensagens o relogio local segue andando para o HUD nao travar.
        if (this.timeLeft > 0) {
          this.timeLeft -= step;
          if (this.timeLeft < 0) this.timeLeft = 0;
        }
        return;
      }

      if (this.state === 'lobby' && this.players.size >= MATCH.MIN_PLAYERS) {
        this._beginPlaying();
      }
    } catch (err) {
      this._warnOnce('update', err);
    }
  }

  // =========================================================================
  // mensagens do servidor
  // =========================================================================

  _bindNet() {
    if (this._bound) return false;
    const net = this.ctx.net;
    if (!net || typeof net.on !== 'function') return false;

    this._offNet.push(net.on(S2C.JOINED, (m) => this._onJoined(m)));
    this._offNet.push(net.on(S2C.PLAYER_JOIN, (m) => this._onPlayerJoin(m)));
    this._offNet.push(net.on(S2C.PLAYER_LEAVE, (m) => this._onPlayerLeave(m)));
    this._offNet.push(net.on(S2C.KILL, (m) => this._onKill(m)));
    this._offNet.push(net.on(S2C.SCORE, (m) => this._onScore(m)));
    this._offNet.push(net.on(S2C.MATCH_END, (m) => this._onMatchEnd(m)));
    this._offNet.push(net.on(S2C.ERROR, (m) => this._onError(m)));
    this._bound = true;
    return true;
  }

  /**
   * Reentra na MESMA sala depois de uma reconexao. Se a sala nao existir mais,
   * o servidor responde com erro e o caminho normal de erro assume - o que
   * importa e o jogador saber, em vez de ficar num mundo vazio.
   */
  _rejoinAfterReconnect() {
    const net = this.ctx.net;
    if (!net || typeof net.send !== 'function') return;
    const room = this.room?.id ?? null;
    this._emit(this.ctx.EVENTS.NOTICE, { text: 'RECONECTANDO À PARTIDA...', duration: 3 });
    // Limpa os remotos: os ids do servidor sao outros depois da reconexao, e
    // manter os antigos deixaria bonecos fantasmas parados na arena.
    try { this.ctx.remotePlayers?.clear?.(); } catch { /* cena ja desmontada */ }
    try { net.send(joinMsg(room, this._mapaLocal())); } catch (err) { this._warnOnce('rejoin', err); }
  }

  /**
   * Aplica posicao, olhar e vida do jogador local a partir da linha que o
   * servidor mandou em `joined`. Espelha o que o _onRespawn do NetSync ja faz
   * ao renascer - a diferenca e que ali existe uma mensagem `respawn` e aqui a
   * informacao vem embutida no roster.
   */
  _placeLocalPlayer(list) {
    if (this.you === null) return;
    const eu = list.find((p) => p && (p.id | 0) === this.you);
    if (!eu) return;

    const ctx = this.ctx;
    try {
      const p = eu.p;
      if (Array.isArray(p) && p.length >= 3) {
        ctx.controller?.setPosition?.(+p[0] || 0, +p[1] || 0, +p[2] || 0);
      }
      // pitch 0 SEMPRE: o olhar horizontal e o unico comeco de partida honesto.
      ctx.cameraRig?.setPose?.(null, Number.isFinite(eu.y) ? eu.y : 0, 0);

      const player = ctx.player;
      if (player) {
        if (typeof player.reset === 'function') player.reset();
        else player.alive = true;
        if (Number.isFinite(eu.hp)) player.health = Math.max(0, eu.hp);
      }
    } catch (err) {
      this._warnOnce('placeLocal', err);
    }
  }

  /** Qual cenario ESTE cliente construiu no boot. */
  _mapaLocal() {
    return String(this.ctx.level?.mapId || this.ctx.settings?.mapId || 'setor7');
  }

  /**
   * A sala veio de outro mapa? Isso nao da para acomodar em tempo de execucao: o
   * cenario e construido uma vez, no boot. Avisa, sai da partida e oferece a
   * troca - melhor que deixar o jogador num jogo paralelo, trocando tiro com
   * gente que ele nao enxerga porque esta desenhando outro lugar.
   */
  _conferirMapa(room) {
    const daSala = String(room?.map || 'setor7');
    const local = this._mapaLocal();
    if (daSala === local) return true;

    this._emit(this.ctx.EVENTS.NOTICE, {
      text: `ESTA PARTIDA É NO MAPA ${daSala.toUpperCase()} - TROCANDO...`,
      duration: 5
    });
    try {
      this.ctx.settings?.set?.('mapId', daSala);
    } catch (err) {
      this._warnOnce('trocaMapa', err);
    }
    this.leaveMatch('mapa diferente');
    // O recarregamento e o unico caminho honesto aqui, e e curto. Fica atras de
    // um tempo pequeno para o aviso aparecer antes da tela sumir.
    setTimeout(() => { try { window.location.reload(); } catch { /* sem window */ } }, 1200);
    return false;
  }

  _onJoined(m) {
    if (!m) return;
    this._applyJoined(m);
  }

  _applyJoined(m) {
    if (!this._conferirMapa(m.room)) return;
    this._joinedOnce = true;
    const room = m.room || null;
    this.room = room ? {
      id: room.id ?? null,
      name: String(room.name || 'SALA'),
      mode: String(room.mode || MATCH.MODE),
      map: String(room.map || 'setor7'),
      scoreLimit: num(room.scoreLimit, MATCH.SCORE_LIMIT),
      timeLimit: num(room.timeLimit, MATCH.TIME_LIMIT)
    } : null;

    this.mode = this.room?.mode || MATCH.MODE;
    this.scoreLimit = this.room?.scoreLimit ?? MATCH.SCORE_LIMIT;
    this.timeLimit = this.room?.timeLimit ?? MATCH.TIME_LIMIT;
    this.timeLeft = this.timeLimit;
    this.you = Number.isFinite(m.you) ? (m.you | 0) : null;
    this.standings = null;
    this.matchId = null;

    this.players.clear();
    this._rosterDirty = true;
    const list = Array.isArray(m.players) ? m.players : [];
    for (let i = 0; i < list.length; i++) this._upsert(list[i]);

    // A cena precisa saber quem sou eu antes do primeiro snapshot, senao o
    // proprio jogador ganharia um boneco remoto em cima da camera.
    const remotes = this.ctx.remotePlayers;
    try {
      remotes?.setLocalId?.(this.you);
      // Quem ja estava na sala nao gera `playerJoin`: entram por aqui.
      for (let i = 0; i < list.length; i++) {
        const p = list[i];
        if (!p || !Number.isFinite(p.id)) continue;
        if (this.you !== null && (p.id | 0) === this.you) continue;
        remotes?.add?.(p);
      }
    } catch (err) {
      this._warnOnce('remotePlayers', err);
    }

    // COLOCA O JOGADOR LOCAL NO LUGAR QUE O SERVIDOR ESCOLHEU.
    // O servidor ja manda posicao, yaw e vida da propria linha dentro de
    // `joined`, e isso estava sendo ignorado: o jogador entrava na partida com a
    // pose que tinha no MENU - inclusive o pitch da camera de apresentacao, que
    // e bem inclinado. Na pratica, todo mundo nascia olhando para o chao.
    this._placeLocalPlayer(list);

    try { this.ctx.netSync?.setActive?.(true); }
    catch (err) { this._warnOnce('netSync', err); }

    this._emit(EVENTS.MATCH_JOINED, {
      room: this.room,
      players: this._roster(),
      you: this.you
    });

    // MATCH.MIN_PLAYERS ainda nao atingido: fica no lobby e a partida comeca
    // sozinha no update quando chegar gente.
    if (this.players.size >= MATCH.MIN_PLAYERS) this._beginPlaying();
    else this._setState('lobby');
  }

  _beginPlaying() {
    this._setState('playing');
    this._emit(EVENTS.GAME_STATE, { state: 'playing' });
  }

  _onPlayerJoin(m) {
    const p = m?.player;
    if (!p || !Number.isFinite(p.id)) return;
    this._upsert(p);
    this._emit(EVENTS.MATCH_SCORE, { players: this._roster(), timeLeft: this.timeLeft });
  }

  _onPlayerLeave(m) {
    if (!m || !Number.isFinite(m.id)) return;
    if (this.players.delete(m.id | 0)) {
      this._rosterDirty = true;
      this._emit(EVENTS.MATCH_SCORE, { players: this._roster(), timeLeft: this.timeLeft });
    }
  }

  /**
   * Baixa. O placar completo vem logo depois no `score`, mas contar aqui deixa o
   * numero na tela reagir no mesmo instante da morte, sem esperar o broadcast.
   */
  _onKill(m) {
    if (!m) return;
    if (Number.isFinite(m.killer)) {
      const k = this._ensure(m.killer | 0);
      if (m.killer !== m.victim) {
        k.kills++;
        k.score += MATCH.SCORE_KILL + (m.headshot ? MATCH.SCORE_HEADSHOT_BONUS : 0);
      }
    }
    if (Number.isFinite(m.victim)) {
      const v = this._ensure(m.victim | 0);
      v.deaths++;
      v.score = Math.max(0, v.score - MATCH.SCORE_DEATH_PENALTY);
    }
    this._rosterDirty = true;
  }

  _onScore(m) {
    if (!m) return;
    const list = Array.isArray(m.players) ? m.players : [];
    for (let i = 0; i < list.length; i++) this._upsert(list[i]);
    if (Number.isFinite(m.timeLeft)) this.timeLeft = Math.max(0, m.timeLeft);
    this._rosterDirty = true;

    // O rtt quem mede e o servidor (o protocolo nao tem ping de cliente):
    // repassamos para o NetClient para a UI ter um numero unico e verdadeiro.
    if (this.you !== null) {
      const me = this.players.get(this.you);
      if (me && Number.isFinite(me.ping)) {
        try { this.ctx.net?.setPing?.(me.ping); } catch (err) { this._warnOnce('setPing', err); }
      }
    }

    this._emit(EVENTS.MATCH_SCORE, { players: this._roster(), timeLeft: this.timeLeft });
  }

  /**
   * Fim de partida. Para de simular e deixa a tela final aparecer - quem desenha
   * o placar final e o Scoreboard, que escuta MATCH_END. Aqui nao se desenha nada.
   */
  _onMatchEnd(m) {
    if (!m) return;
    this.standings = Array.isArray(m.standings) ? m.standings : [];
    this.matchId = m.matchId ?? null;
    this.timeLeft = 0;

    // Coloca a colocacao final no placar que a interface ja tem em maos.
    for (let i = 0; i < this.standings.length; i++) {
      const s = this.standings[i];
      if (!s || !Number.isFinite(s.id)) continue;
      const p = this._ensure(s.id | 0);
      if (s.nickname) p.nickname = String(s.nickname);
      p.kills = num(s.kills, p.kills);
      p.deaths = num(s.deaths, p.deaths);
      p.score = num(s.score, p.score);
      p.place = num(s.place, p.place);
    }
    this._rosterDirty = true;

    // Para de mandar estado: a partida terminou, ninguem mais se move em jogo.
    try { this.ctx.netSync?.setActive?.(false); } catch (err) { this._warnOnce('netSync', err); }

    // Solta o ponteiro para o jogador conseguir clicar na tela de placar final.
    try { this.ctx.input?.releaseLock?.(); } catch (err) { this._warnOnce('releaseLock', err); }

    this._setState('ended');
    this._emit(EVENTS.MATCH_END, { standings: this.standings, matchId: this.matchId });
  }

  /**
   * Todo NET_ERROR vira aviso na tela. Sem isto o jogador e desconectado em
   * silencio ("sua conta entrou em outro lugar", limite de mensagens, servidor
   * reiniciando) e fica olhando uma arena que parou de responder sem saber por que.
   */
  _noticeError(code, message) {
    const texto = message || this._textoDoErro(code);
    if (!texto) return;
    this._emit(this.ctx.EVENTS.NOTICE, { text: texto, duration: 6 });
  }

  _textoDoErro(code) {
    switch (code) {
      case ERR.ROOM_FULL: return 'A sala está cheia. Tente de novo em instantes.';
      case ERR.ROOM_NOT_FOUND: return 'Sala não encontrada.';
      case ERR.BAD_TOKEN:
      case ERR.NOT_AUTHENTICATED: return 'Sua sessão expirou. Entre na conta de novo.';
      case ERR.RATE_LIMITED: return 'Muitas mensagens em pouco tempo. Aguarde um momento.';
      default: return 'Falha na conexão com o servidor da partida.';
    }
  }

  _onError(m) {
    if (!m) return;
    const code = String(m.code || ERR.SERVER_ERROR);
    const message = m.message ? String(m.message) : '';
    // Aviso PRIMEIRO e sempre: antes, erro que chegasse com a partida em curso
    // (limite de mensagens, conta aberta em outro lugar, servidor reiniciando)
    // era descartado aqui e o jogador nao ficava sabendo de nada.
    this._noticeError(code, message);
    this._emit(this.ctx.EVENTS.NET_ERROR, { code, message });
    if (FATAL_CODES.indexOf(code) >= 0 && (this.state === 'connecting' || this.state === 'lobby')) {
      this._giveUp(message || code);
    }
  }

  _onNetStatus(p) {
    // RECONECTOU no meio da partida. O servidor trata a conexao nova como um
    // jogador NOVO (playerId novo, sem sala), e o `join` so sai daqui - ou seja,
    // sem este reenvio o jogador volta a um mundo vazio: os remotos somem pelo
    // tempo limite, o placar congela e o estado que ele manda e descartado em
    // silencio. Nenhum aviso apareceria, porque a reconexao DEU CERTO.
    if (p?.state === 'online' && this.isActive() && this._joinedOnce) {
      this._rejoinAfterReconnect();
      return;
    }
    if (p?.state !== 'error' || !this.isActive()) return;
    // Reconexao esgotada: a partida terminou para este cliente.
    this._teardown('conexao perdida');
    this._setState('idle');
    this.lastReason = 'conexao perdida';
    this._emit(EVENTS.MATCH_LEFT, { reason: 'conexao perdida' });
    this._emit(EVENTS.GAME_STATE, { state: 'menu' });
  }

  // =========================================================================
  // roster
  // =========================================================================

  /** Cria (ou devolve) a linha de um jogador. Sem alocar quando ja existe. */
  _ensure(id, nickname) {
    let p = this.players.get(id);
    if (!p) {
      p = { id, nickname: String(nickname || `JOGADOR ${id}`), kills: 0, deaths: 0, score: 0, ping: 0, place: 0 };
      this.players.set(id, p);
      this._rosterDirty = true;
    } else if (nickname) {
      p.nickname = String(nickname);
    }
    return p;
  }

  /** Aplica uma linha vinda do servidor sobre a linha local, campo por campo. */
  _upsert(src) {
    if (!src || !Number.isFinite(src.id)) return null;
    const p = this._ensure(src.id | 0, src.nickname || src.name);
    if (Number.isFinite(src.kills)) p.kills = src.kills;
    if (Number.isFinite(src.deaths)) p.deaths = src.deaths;
    if (Number.isFinite(src.score)) p.score = src.score;
    if (Number.isFinite(src.ping)) p.ping = src.ping;
    if (Number.isFinite(src.place)) p.place = src.place;
    this._rosterDirty = true;
    return p;
  }

  // =========================================================================
  // interno
  // =========================================================================

  /** Desmonta a sessao online sem mexer no estado da maquina nem emitir evento. */
  _teardown(reason) {
    try { this.ctx.netSync?.setActive?.(false); } catch (err) { this._warnOnce('netSync', err); }
    try { this.ctx.remotePlayers?.clear?.(); } catch (err) { this._warnOnce('remotePlayers', err); }
    this.players.clear();
    this._rosterDirty = true;
    this.room = null;
    this.you = null;
    this.timeLeft = 0;
    this.standings = null;
    this.matchId = null;
    this.lastReason = String(reason || '');
  }

  /** Desistiu de entrar: desfaz tudo e volta ao menu com o motivo na mao. */
  _giveUp(message) {
    const why = String(message || 'falha na partida');
    this._teardown(why);
    this._setState('idle');
    this._noticeError(ERR.SERVER_ERROR, why);
    this._emit(EVENTS.NET_ERROR, { code: ERR.SERVER_ERROR, message: why });
    this._emit(EVENTS.MATCH_LEFT, { reason: why });
    this._emit(EVENTS.GAME_STATE, { state: 'menu' });
    try { this.ctx.net?.disconnect?.(why); } catch (err) { this._warnOnce('disconnect', err); }
  }

  _setState(next) {
    if (this.state === next) return;
    this.state = next;
  }

  /** Espera o socket ficar online. Fora do laco de frame: pode usar timer. */
  async _waitOnline(net, timeoutSec) {
    const deadline = Date.now() + timeoutSec * 1000;
    while (Date.now() < deadline) {
      if (net.state === 'online') return true;
      // 'error' e desistencia definitiva do NetClient (gastou as tentativas);
      // 'connecting' e 'offline' (entre duas tentativas) continuam valendo espera.
      if (net.state === 'error') return false;
      await sleep(POLL_MS);
    }
    return net.state === 'online';
  }

  /**
   * Espera UMA mensagem de um tipo, com prazo. Devolve a mensagem ou null no
   * estouro - quem chama trata null, nunca precisa de try/catch.
   */
  _once(type, timeoutSec) {
    const net = this.ctx.net;
    if (!net || typeof net.on !== 'function') return Promise.resolve(null);
    return new Promise((resolve) => {
      let done = false;
      let timer = null;
      const off = net.on(type, (msg) => {
        if (done) return;
        done = true;
        if (timer !== null) clearTimeout(timer);
        try { off(); } catch (err) { this._warnOnce('once.off', err); }
        resolve(msg);
      });
      timer = setTimeout(() => {
        if (done) return;
        done = true;
        try { off(); } catch (err) { this._warnOnce('once.off', err); }
        resolve(null);
      }, Math.max(1, timeoutSec * 1000));
    });
  }

  _emit(type, payload) {
    try {
      this.ctx.events?.emit?.(type, payload);
    } catch (err) {
      this._warnOnce('emit', err);
    }
  }

  _warnOnce(key, err) {
    if (this._warned.has(key)) return;
    this._warned.add(key);
    console.warn(`[MatchState] falha em "${key}" - seguindo sem isso:`, err);
  }
}
